// utils/arabicSearch.js
//
// A forgiving match for typed Egyptian-Arabic shop names (tips-wave P3-A,
// U-79). Members spell the same shop differently — أ/إ/آ for the same
// glottal stop, ة/ه interchangeably at a word's end, ى/ي interchangeably,
// and a stray tatweel or harakah copied in from somewhere else — and the
// roster picker's filter used to be a bare case-folded `.includes()`, so
// «ريهام جاليري» typed for a roster row spelled «جاليري ريهام» found
// nothing at all.
//
// Two rules, and both come from the real 33-row roster this shipped against:
//   1. Fold the SPELLING variants that mean nothing to a reader but break a
//      literal substring match — never fold anything that changes the word.
//   2. Match on WORDS, not on the whole string in order: a query is a hit
//      when every one of its words appears somewhere in the name, any order.
//
// Pure functions — no Vue, no F7, no i18n — so this stays testable without
// mounting, the same discipline `supplierSuggestion.js` keeps.

const ARABIC_DIACRITICS = /[ؐ-ًؚ-ٰٟۖ-ۭ]/g;
const TATWEEL = /ـ/g;

/**
 * Fold the spelling variants a member's own typing carries, so two spellings
 * of the same shop compare equal. Never folds anything that changes what word
 * it is (ا/ح/ه stay themselves; only أ/إ/آ collapse to ا, and only the
 * word-shape marks — tatweel, harakat — are stripped).
 *
 * @param {string} text
 * @returns {string}
 */
export function normalizeArabic(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(ARABIC_DIACRITICS, '')
    .replace(TATWEEL, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Does every word of `query` appear somewhere in `name`, in ANY order? An
 * empty (or whitespace-only) query matches everything — the roster's own
 * unfiltered state.
 *
 * @param {string} query what she typed
 * @param {string} name a roster shop's name
 * @returns {boolean}
 */
export function matchesAllWords(query, name) {
  const words = normalizeArabic(query).split(' ').filter(Boolean);
  if (words.length === 0) return true;

  const normalizedName = normalizeArabic(name);
  return words.every((word) => normalizedName.includes(word));
}
